import { type ReactNode, useId } from 'react';

import { Link } from '../../app/router.tsx';

import type { ActivityItem, FeathersInfo, WeekDay } from '../../nests/api.ts';
import {
	activityLabel,
	activityTime,
	canSayWellDone,
	type FeatherRow,
	LEVEL_LABEL,
	type LevelCard,
	type LevelRow,
	levelRowSpoken,
	type NameOf,
	ordinal,
	weekSpoken,
} from '../../nests/model.ts';
import type { Level } from '@arrow/puzzle-schema';
import { busyButton } from '../../components/busy.ts';
import { Popover, PopoverContent, PopoverTrigger, Tabs, TabsContent, TabsList, TabsTrigger, Toggle } from '../../ui/index.ts';
import { Feather, InfoMark } from './parts.tsx';

// --------------------------------------------------------------------------- Today's times

/** First place's mark: a small lime tick beside the time (not an underline, which reads as a link). */
function FirstTick() {
	return (
		<span className="tick qk" aria-hidden="true">
			<svg viewBox="0 0 16 16">
				<path d="M3.5 8.5l3 3 6-7" />
			</svg>
		</span>
	);
}

/** Your row's way in to today's puzzle at the chosen level, while you haven't played it: "Play Hard". */
export interface PlayLink {
	level: Level;
	to: string;
}

/**
 * The three level cards (ui/Tabs): the level and its leading time (1st), and whose. One Tab
 * stop; the arrow keys, Home and End move along and choose. The chosen level's panel
 * (`children`, the list below) is named by its card.
 */
export function LevelTabs({ cards, value, onChange, children }: { cards: readonly LevelCard[]; value: Level; onChange: (level: Level) => void; children: ReactNode }) {
	return (
		<Tabs value={value} onValueChange={(v) => onChange(v as Level)} className="nest-flat">
			<TabsList className="lvcards" aria-label="Level" data-n={cards.length}>
				{cards.map((c) => (
					<TabsTrigger
						key={c.level}
						value={c.level}
						className="lvcard"
						aria-label={`${c.label}, ${c.leader ? `${c.leader.name} ${c.leader.time}` : 'no time yet'}`}
					>
						{/* The time never truncates: it sits beside the level; a long name below gives way. */}
						<span className="lv-top">
							<span className="lv">{c.label}</span>
							{c.leader && <span className="lvt">{c.leader.time}</span>}
						</span>
						{c.leader ? <span className="lvq">{c.leader.name}</span> : <span className="lvq none">–</span>}
					</TabsTrigger>
				))}
			</TabsList>
			<TabsContent value={value} className="lvpanel">
				{children}
			</TabsContent>
		</Tabs>
	);
}

/**
 * One level's ranked list (Nest boards v2): place, name, time, in the api's rank; 1st
 * ticked (ties too), "N reveals" under a time that used them. Not finished: a Nudge button
 * for anyone who's played nothing today, else the status ("Playing") where the time goes. Your row while you haven't played this
 * level sits on top with "Play {Level}" where the time goes; otherwise it's highlighted in
 * place. Once nudged the button stays, quietly done, so focus has somewhere to stay.
 */
export function LevelList({
	level,
	rows,
	nudged,
	onNudge,
	play = null,
}: {
	level: Level;
	rows: readonly LevelRow[];
	nudged: ReadonlySet<string>;
	onNudge: (row: LevelRow) => void;
	play?: PlayLink | null;
}) {
	return (
		<ul className="rank" aria-label="Today’s times" data-level={level}>
			{rows.map((r) => {
				// No time yet reads quietly, except your own row while it offers Play: that's the next thing to do.
				const cls = `rr${r.me ? ' me' : ''}${r.time === null && !(r.play && play) ? ' quiet' : ''}`;
				// One thing where the time goes: Play, the time, Nudge (which says "not played"), or the status.
				let right: ReactNode = null;
				if (r.play && play) {
					right = (
						<Link to={play.to} className="rr-play">
							Play {LEVEL_LABEL[play.level]}
						</Link>
					);
				} else if (r.time !== null) {
					right = (
						<span className="rr-t" aria-hidden="true">
							<span className={`tm${r.first ? ' best' : ''}`}>
								{r.time}
								{r.first && <FirstTick />}
							</span>
							{r.reveals && <small className="rv">{r.reveals}</small>}
						</span>
					);
				} else if (r.nudge) {
					const done = nudged.has(r.memberId);
					right = (
						<button type="button" className="rr-nudge" aria-label={`${done ? 'Nudged' : 'Nudge'} ${r.name}`} {...busyButton(done, () => onNudge(r))} aria-busy={undefined}>
							{done ? 'Nudged' : 'Nudge'}
						</button>
					);
				} else if (r.status) {
					right = (
						<span className="rr-st" aria-hidden="true">
							{r.status}
						</span>
					);
				}
				return (
					<li key={r.memberId} className={cls}>
						<span className="sr-only">{levelRowSpoken(r)}</span>
						<span className="n" aria-hidden="true">
							{r.place ?? ''}
						</span>
						<span className="who" aria-hidden="true">
							<span className="nm">{r.name}</span>
						</span>
						{right}
					</li>
				);
			})}
		</ul>
	);
}

// ------------------------------------------------------------------- feathers and the ⓘ

/**
 * "Feathers ⓘ" (ui/Popover): a real button (aria-expanded, aria-controls, a 44px target)
 * opening the printed popover, hung just under it, right edges together. Tapping it again,
 * tapping anywhere else, Escape or tabbing on closes it; after Escape, focus goes back to
 * the button. Focus stays on the button while it's open; the card is a note, not a dialog,
 * rendered in place (`inline`), so it's read straight after the button, inside <main>.
 */
export function FeathersLabel({ info, open, onToggle }: { info: FeathersInfo | null; open: boolean; onToggle: (open: boolean) => void }) {
	const id = useId();
	return (
		<div className="lb-hd">
			<Popover open={open} onOpenChange={onToggle}>
				<PopoverTrigger
					type="button"
					className="fx-btn"
					aria-controls={id}
					aria-haspopup={undefined}
					// Safari doesn't focus a tapped button: keep focus here, for Escape and tabbing on.
					onClick={(e) => e.currentTarget.focus()}
				>
					Feathers
					<InfoMark />
					<span className="sr-only">: how they’re earned</span>
				</PopoverTrigger>
				<PopoverContent
					inline
					className="fx-pop"
					id={id}
					role="note"
					data-testid="feathers-pop"
					// Where it always hung: 2px up into the button's foot, 2px past its right edge.
					sideOffset={-2}
					alignOffset={-2}
					collisionPadding={12}
					// The card explains; focus stays on the button (tabbing on closes it).
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<span>{info?.text ?? 'Feathers come from solving the day’s puzzles.'}</span>
					{info?.numbers && <span className="fx-n">{info.numbers}</span>}
				</PopoverContent>
			</Popover>
		</div>
	);
}

/** The week strip: a square a day, Monday to Sunday (played, played with reveals, today outlined). */
function WeekStrip({ days, today }: { days: readonly WeekDay[]; today: string }) {
	return (
		<span className="wk" aria-hidden="true">
			{days.map((d) => (
				<i key={d.date} className={`${d.state === 'none' ? '' : d.state === 'played' ? 'p' : 'r'}${d.date === today ? ' t' : ''}`} />
			))}
		</span>
	);
}

/** A label under a name; a screen reader hears it after a comma. */
function Tag({ className, children }: { className: string; children: ReactNode }) {
	return (
		<span className={className}>
			<span className="sr-only">, </span>
			{children}
		</span>
	);
}

/** The strip's key, under This week's list. */
export function WeekLegend() {
	return (
		<p className="wk-key" aria-hidden="true">
			<span>
				<span className="wk">
					<i className="p" />
				</span>
				Played
			</span>
			<span>
				<span className="wk">
					<i className="r" />
				</span>
				Reveals
			</span>
			<span>
				<span className="wk">
					<i className="t" />
				</span>
				Today
			</span>
		</p>
	);
}

/**
 * This week / All time: a header row (an "Ends Sun" tag on the week, and the Feathers ⓘ
 * button on the right), then the ranked list with your row highlighted in place.
 */
export function FeatherList({
	rows,
	label,
	todayKey,
	tag = null,
	header,
}: {
	rows: readonly FeatherRow[];
	label: string;
	todayKey: string;
	tag?: string | null;
	header: ReactNode;
}) {
	return (
		<>
			<div className="colh">
				<span />
				<span>{tag && <span className="tag">{tag}</span>}</span>
				{header}
			</div>
			<ol className="rank lb" aria-label={label}>
				{rows.map((r) => (
					<li key={r.memberId} className={`rr${r.me ? ' me' : ''}${r.feathers === 0 ? ' quiet' : ''}`}>
						<span className="n">
							<span aria-hidden="true">{r.rank}</span>
							<span className="sr-only">{ordinal(r.rank)}, </span>
						</span>
						<span className="who">
							<span className="nm">{r.name}</span>
							{r.week && (
								<>
									<WeekStrip days={r.week} today={todayKey} />
									<span className="sr-only">, {weekSpoken(r.week)}</span>
								</>
							)}
							{!r.week && (r.isNew || r.days || r.reveals) && (
								<span className="tags">
									{r.isNew && <Tag className="tag st">New</Tag>}
									{r.days && <Tag className="tag">{r.days}</Tag>}
									{r.reveals && <Tag className="tag rv">{r.reveals}</Tag>}
								</span>
							)}
						</span>
						<span className="rr-t">
							<span className="tm fv">
								{r.feathers.toLocaleString('en-GB')}
								<Feather />
								<span className="sr-only"> feathers</span>
							</span>
							{r.week && r.reveals && (
								<small className="rv">
									<span className="sr-only">, </span>
									{r.reveals}
								</small>
							)}
						</span>
					</li>
				))}
			</ol>
		</>
	);
}

// ---------------------------------------------------------------------------- Activity

/**
 * Today's activity, under the list: each item a label (who · what) and its time, with one
 * toggling "Well done" on other people's. Nothing to show (loading, or nothing yet today): no
 * section at all.
 */
export function ActivityFeed({
	items,
	nameOf,
	me,
	onWellDone,
	status,
}: {
	items: readonly ActivityItem[];
	nameOf: NameOf;
	me: string;
	onWellDone: (item: ActivityItem) => void;
	status: 'loading' | 'error' | 'ready';
}) {
	if (status === 'loading' || (status === 'ready' && items.length === 0)) return null;
	return (
		<>
			{/* A group heading (UX-002), so it's on the page's outline. */}
			<h2 className="h-group act-h">Activity</h2>
			<div className="feed">
				{status === 'error' && <p className="dnote">Couldn’t load the activity. Check your connection.</p>}
				{items.length > 0 && (
					<ul className="feed-list">
						{items.map((item) => {
							const l = activityLabel(item, nameOf, me);
							const when = activityTime(item.at);
							return (
								<li key={item.id} className="fi">
									<span className="tx">
										<span className="tx-l">
											<b>{l.who}</b> <span className="tx-w">· {l.what}</span>
										</span>
										{when && <span className="tx-when">{when}</span>}
									</span>
									{canSayWellDone(item, me) ? (
										// "Well done, Ellie": one per person, so each is told apart in a list of buttons (A11Y-18).
										// ui/Toggle (Radix): aria-pressed is Radix's; the look is .rxp's.
										<Toggle className="rxp" pressed={item.wellDone.mine} aria-label={`Well done, ${nameOf(item.memberId)}`} onPressedChange={() => onWellDone(item)}>
											Well done
										</Toggle>
									) : item.memberId === me && item.kind === 'solve' && item.wellDone.count > 0 ? (
										<span className="rx-n">Well done · {item.wellDone.count}</span>
									) : null}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</>
	);
}
